/**
 * EngineDetailScreenshot Component
 *
 * Engine-specific detail view for the Screenshot engine, used in the
 * print/PDF tree. Delegates to the v2 `PrintScreenshotsSection`, which
 * composes the same pure sections that drive the interactive surface
 * (`InsightsSection`, `ScoreBreakdownSection`, `ScreenshotDetailCard`,
 * `RecommendationsSection`) without depending on any context provider.
 *
 * The `audit` prop is required because `PrintScreenshotsSection` derives its
 * full view-model from `extractScreenshotsViewModel(audit)`, which needs the
 * full audit envelope (e.g. `audit.screenshots`, cross-engine linkages on
 * recommendations). Until A7Y-660 the print body re-derived a partial
 * `metrics` object from `analysis` + `detail.score_breakdown`; that path is
 * gone now that the v2 sections are the single source of truth.
 */

import type { ScreenshotEngineSpecificAnalysis, UnifiedDetailedAnalysis } from "@a7y/types";
import type { AuditOutputV2 } from "@a7y/audit-output-v2";
import { PrintScreenshotsSection } from "@/components/audit/engines/screenshots/PrintScreenshotsSection";

export interface EngineDetailScreenshotProps {
  /** Screenshot-specific analysis data (kept for type compatibility with the dispatcher; v2 reads from `audit`). */
  analysis: ScreenshotEngineSpecificAnalysis;
  /** Detailed analysis with score_breakdown (kept for type compatibility). */
  detail: UnifiedDetailedAnalysis;
  /** When true, force-expands all collapsible sections (for print/PDF). */
  printMode?: boolean;
  /** V2 audit envelope — required for `PrintScreenshotsSection`. */
  audit?: AuditOutputV2;
  /** Additional CSS classes */
  className?: string;
}

export function EngineDetailScreenshot({ audit, className = "" }: EngineDetailScreenshotProps) {
  // Without the full audit payload there is nothing to render — the legacy
  // metrics-only path has been retired in favor of the v2 view-model.
  if (!audit) return null;

  return (
    <div className={`space-y-6 ${className}`}>
      <PrintScreenshotsSection audit={audit} />
    </div>
  );
}
